import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useGetThreadQuery } from "../../../redux/queries/conversations"
import Footer from "./footer"
import Header from "./header"
import { useAppDispatch, useUserLastActivity } from "../../hooks"
import {
  currentThread,
  setCurrentThread
} from "../../../redux/slices/conversations"
import { ConversationItems, Thread, Threads } from "../../../types"
import { useSelector } from "react-redux"
import ThreadItemList from "./thread-item-list"
import { ConversationsThreadSkeleton } from "../skeletons"

interface Props {
  threads: Threads
}

const ConversationsThread = ({ threads }: Props) => {
  const { _id = "" } = useParams<{ _id: string }>()

  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState(true)
  const [previousThreadID, setPreviousThreadID] = useState<string | null>(null)

  const lastActivity = useUserLastActivity()

  const item = useMemo(
    () => threads?.find(thread => thread._id === _id),
    [_id, threads]
  )

  const item_id = item?.item_id ?? ""
  const itemTypeKey = item?.type === "lead" ? "lead_id" : "subscription_id"

  const { data, isFetching, isError, isSuccess, isLoading, isUninitialized } =
    useGetThreadQuery(
      {
        [itemTypeKey]: item_id
      },
      // Poll every 5 seconds while there is a selected thread and there has been activity within the last 3 minutes
      {
        skip: !item_id || Date.now() - lastActivity > 180000,
        pollingInterval: 5000
      }
    )

  const thread = useSelector(currentThread)

  const updateCurrentThread = useCallback(
    (thread: Thread) => {
      dispatch(setCurrentThread(thread))
    },
    [dispatch]
  )

  useEffect(() => {
    if (isSuccess && data) {
      updateCurrentThread(data)
    }
  }, [data, isSuccess, updateCurrentThread])

  useEffect(() => {
    if (_id !== previousThreadID) {
      setLoading(true) // Start loading when threadID changes
      setPreviousThreadID(_id) // Update the previousThreadID
    }
  }, [_id, previousThreadID])

  // Monitor `isFetching` to determine when data has been fetched
  useEffect(() => {
    if (loading && !isFetching) {
      setLoading(false) // Stop loading once the data has been fetched
    }
  }, [isFetching, loading])

  const conversation: ConversationItems = useMemo(() => {
    const messages = thread?.messages ?? []
    const events = thread?.events ?? []
    return [...messages, ...events].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
  }, [thread?.messages, thread?.events])

  const scrollRef = useRef<HTMLDivElement>(null)

  // Scroll to bottom logic
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [])

  const [isUserAtBottom, setIsUserAtBottom] = useState(true)

  // Function to check if user is at the bottom
  const isAtBottom = () => {
    const scrollContainer = scrollRef.current
    if (scrollContainer) {
      return (
        scrollContainer.scrollHeight - scrollContainer.scrollTop ===
        scrollContainer.clientHeight
      )
    }
    return false
  }

  // Scroll to the bottom when new messages arrive if the user is at the bottom
  useEffect(() => {
    const scrollContainer = scrollRef.current
    if (isUserAtBottom) {
      scrollToBottom()
    }

    const handleScroll = () => {
      if (isAtBottom()) {
        setIsUserAtBottom(true)
      } else {
        setIsUserAtBottom(false)
      }
    }

    scrollContainer?.addEventListener("scroll", handleScroll)
    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll)
    }
  }, [conversation, isUserAtBottom, scrollToBottom])

  // Scroll to bottom when the component is mounted or conversation updates
  useEffect(() => {
    scrollToBottom()
  }, [conversation, scrollToBottom]) // When the conversation changes (new message), scroll to bottom

  if (loading || isLoading || isUninitialized) {
    return <ConversationsThreadSkeleton />
  }

  if (isError || !thread) {
    return (
      <div className="items-center justify-center w-full pt-12 flex">
        Error loading thread or no thread found.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-hidden">
        <div ref={scrollRef} className="h-full w-full overflow-y-auto">
          <Header thread={thread} />
          <ThreadItemList
            conversation={conversation}
            thread={thread}
            onResize={scrollToBottom}
          />
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default ConversationsThread
